import { useCallback, useEffect, useState } from "react";
import {
  friendlyError,
  partnerApi,
  type PartnerMe,
  type PartnerModel,
  type PartnerProgram,
} from "@/lib/api";
import { Button, Card, ErrorText, Field, PageLoader, Row, Stat, usd, rub } from "@/components/ui";

const BOT_LINK = "https://t.me/ttex_robot";
const WEB_LINK = "https://ttex.online";

export function Dashboard() {
  const [me, setMe] = useState<PartnerMe | null>(null);
  const [err, setErr] = useState("");

  const load = useCallback(() => {
    setErr("");
    partnerApi.me().then(setMe).catch((e) => setErr(friendlyError(e)));
  }, []);
  useEffect(load, [load]);

  if (err) return <Card className="mt-4"><ErrorText>{err}</ErrorText></Card>;
  if (!me) return <PageLoader />;
  if (!me.is_partner) return <Apply program={me.program} onApplied={load} />;
  if (me.status === "pending")
    return (
      <Card className="mt-4">
        <div className="text-base font-semibold">Заявка на рассмотрении</div>
        <p className="mt-2 text-sm text-white/50">
          Мы проверяем заявки вручную. Как только одобрят — здесь появятся код, ссылка и статистика.
        </p>
      </Card>
    );
  if (me.status === "rejected")
    return <Apply program={me.program} rejectedReason={me.reject_reason} onApplied={load} />;
  if (me.status === "blocked")
    return (
      <Card className="mt-4">
        <div className="text-base font-semibold text-red-300">Партнёрство приостановлено</div>
        <p className="mt-2 text-sm text-white/50">
          {me.reject_reason || "Аккаунт заблокирован. Выплаты недоступны."} Напишите в поддержку.
        </p>
      </Card>
    );

  return <Active me={me} onReload={load} />;
}

function Active({
  me,
  onReload,
}: {
  me: Extract<PartnerMe, { is_partner: true }>;
  onReload: () => void;
}) {
  const code = me.code ?? "";
  const tgLink = code ? `${BOT_LINK}?start=${code}` : "";
  const webLink = code ? `${WEB_LINK}/?ref=${code}` : "";

  return (
    <div className="space-y-3 pt-2">
      <Card>
        <div className="text-xs text-white/45">Доступно к выводу</div>
        <div className="mt-1 text-3xl font-semibold tabular-nums">{usd(me.balance.available_usd)}</div>
        <div className="mt-3 grid grid-cols-3 gap-2">
          <Stat label="В холде" value={usd(me.balance.hold_usd)} />
          <Stat label="Ожидает" value={usd(me.balance.pending_usd)} />
          <Stat label="Выплачено" value={usd(me.balance.paid_usd)} />
        </div>
      </Card>

      {me.balance.available_usd < 0 && (
        <Card className="border-amber-400/20 bg-amber-400/5">
          <p className="text-sm text-amber-200/80">
            Баланс отрицательный: по сделке был возврат после выплаты. Долг спишется с ближайших начислений.
          </p>
        </Card>
      )}

      <Card>
        <div className="text-sm font-medium text-white/70">Статистика</div>
        <div className="mt-2">
          <Row left={<span className="text-white/50">Оборот клиентов</span>} right={rub(me.stats.turnover_rub)} />
          <Row left={<span className="text-white/50">Сделок</span>} right={me.stats.deals} />
          <Row left={<span className="text-white/50">Клиентов</span>} right={me.stats.clients} />
          <Row left={<span className="text-white/50">Переходов по ссылке</span>} right={me.clicks} />
        </div>
      </Card>

      {me.model === "agent" && <AgentMarkup me={me} onSaved={onReload} />}

      <Card>
        <div className="text-xs text-white/45">Ваш код</div>
        <div className="mt-1 font-mono text-2xl font-semibold tracking-wider">{code.toUpperCase()}</div>
        <p className="mt-1 text-xs text-white/40">
          Клиент вводит его в боте вручную — или переходит по вашей ссылке.
        </p>
        <div className="mt-3 space-y-2">
          <CopyRow label="Ссылка в Telegram" value={tgLink} />
          <CopyRow label="Ссылка на сайт" value={webLink} />
          <CopyRow label="Код" value={code.toUpperCase()} />
        </div>
      </Card>

      <Card>
        <div className="text-sm font-medium text-white/70">Как это работает</div>
        <p className="mt-1.5 text-sm text-white/45">
          {me.model === "agent"
            ? "Клиент по вашей ссылке покупает по курсу с вашей наценкой — разница ваш заработок."
            : `С каждой сделки приведённого клиента начисляется ${me.program.l1_pct}% от её оборота.`}{" "}
          Вознаграждение появляется после исполнения сделки, {me.program.hold_days} дн. холд, затем
          доступно к выводу.
        </p>
      </Card>
    </div>
  );
}

function CopyRow({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable */
    }
  };
  return (
    <button type="button" onClick={copy} className="block w-full text-left">
      <div className="rounded-xl bg-surface-raised px-3 py-2.5">
        <div className="text-xs text-white/40">{label}</div>
        <div className="mt-0.5 flex items-center justify-between gap-2">
          <span className="truncate text-sm text-white/80">{value}</span>
          <span className={`shrink-0 text-xs ${copied ? "text-brand" : "text-white/40"}`}>
            {copied ? "Скопировано" : "Копировать"}
          </span>
        </div>
      </div>
    </button>
  );
}

function AgentMarkup({
  me,
  onSaved,
}: {
  me: Extract<PartnerMe, { is_partner: true }>;
  onSaved: () => void;
}) {
  const [value, setValue] = useState(String(me.markup_pct));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const p = Number(value.replace(",", "."));
  const valid = Number.isFinite(p) && p >= 0;
  const max = me.program.max_markup_pct;
  const overCap = max !== null && valid && p > max;
  const preview = me.base_rate !== null && valid ? me.base_rate * (1 + p / 100) : null;

  const save = async () => {
    if (!valid || overCap || busy) return;
    setBusy(true);
    setError("");
    try {
      await partnerApi.setMarkup(p);
      onSaved();
    } catch (e) {
      setError(friendlyError(e, "Не удалось сохранить наценку."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <div className="text-sm font-medium text-white/70">Наценка агента</div>
      <div className="mt-2">
        <Row
          left={<span className="text-white/50">Базовый курс TTEX</span>}
          right={me.base_rate !== null ? rub(me.base_rate) : "—"}
        />
      </div>
      <div className="mt-2">
        <Field
          label="Моя наценка, %"
          type="number"
          inputMode="decimal"
          value={value}
          onChange={(e) => setValue(e.target.value)}
        />
      </div>
      <div className="mt-2">
        <Row
          left={<span className="text-white/50">Итоговый курс клиента</span>}
          right={preview !== null ? rub(preview) : "—"}
        />
      </div>
      {overCap && <ErrorText>Максимальная наценка сейчас — {max}%.</ErrorText>}
      <ErrorText>{error}</ErrorText>
      <Button
        variant="ghost"
        className="mt-3 w-full"
        loading={busy}
        disabled={!valid || overCap || p === me.markup_pct}
        onClick={save}
      >
        Сохранить наценку
      </Button>
    </Card>
  );
}

// ── вступление ──
function Apply({
  program,
  rejectedReason,
  onApplied,
}: {
  program: PartnerProgram;
  rejectedReason?: string | null;
  onApplied: () => void;
}) {
  const [model, setModel] = useState<PartnerModel>("referral");
  const [accepted, setAccepted] = useState(false);
  const [offer, setOffer] = useState("");
  const [showOffer, setShowOffer] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    partnerApi.offer().then((o) => setOffer(o.text)).catch(() => setOffer(""));
  }, []);

  const submit = async () => {
    if (!accepted || busy) return;
    setBusy(true);
    setError("");
    try {
      await partnerApi.apply(model);
      onApplied();
    } catch (e) {
      setError(friendlyError(e, "Не удалось отправить заявку."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3 pt-2">
      <div>
        <div className="text-xl font-bold">Партнёрская программа</div>
        <p className="mt-1 text-sm text-white/50">Приводите клиентов и зарабатывайте с их оборота.</p>
      </div>

      {rejectedReason && (
        <Card className="border-red-500/20 bg-red-500/5">
          <p className="text-sm text-red-200/80">
            Предыдущая заявка отклонена: {rejectedReason}. Можно подать заново.
          </p>
        </Card>
      )}

      <ModelCard
        selected={model === "referral"}
        onSelect={() => setModel("referral")}
        title="Реферал"
        desc={`Фиксированный процент с оборота приведённых клиентов — ${program.l1_pct}%. Клиент покупает по обычному курсу TTEX.`}
      />
      <ModelCard
        selected={model === "agent"}
        onSelect={() => setModel("agent")}
        title="Агент"
        desc={
          program.max_markup_pct === null
            ? "Вы сами задаёте наценку сверх базового курса и зарабатываете на ней."
            : `Вы сами задаёте наценку сверх базового курса — до +${program.max_markup_pct}%.`
        }
      />

      <Card>
        <p className="text-sm text-white/55">
          Вознаграждение начисляется с завершённых сделок, {program.hold_days} дн. холда, вывод от{" "}
          {program.min_payout_usd} $. Персональные данные клиентов вам не видны — только обезличенная
          статистика.
        </p>
      </Card>

      <label className="flex items-start gap-3 text-sm text-white/70">
        <input
          type="checkbox"
          checked={accepted}
          onChange={(e) => setAccepted(e.target.checked)}
          className="mt-0.5 h-5 w-5 shrink-0 accent-brand"
        />
        <span>
          Согласен с{" "}
          <button type="button" className="text-brand underline" onClick={() => setShowOffer((v) => !v)}>
            офертой партнёра
          </button>
        </span>
      </label>

      {showOffer && (
        <Card className="max-h-64 overflow-y-auto">
          <p className="whitespace-pre-wrap text-sm text-white/55">
            {offer || "Текст оферты появится здесь после публикации."}
          </p>
        </Card>
      )}

      <ErrorText>{error}</ErrorText>
      <Button className="w-full" loading={busy} disabled={!accepted} onClick={submit}>
        Отправить заявку
      </Button>
    </div>
  );
}

function ModelCard({
  selected,
  onSelect,
  title,
  desc,
}: {
  selected: boolean;
  onSelect: () => void;
  title: string;
  desc: string;
}) {
  return (
    <button type="button" onClick={onSelect} className="block w-full text-left">
      <div
        className={`rounded-2xl border p-4 transition ${
          selected ? "border-brand/60 bg-brand/10" : "border-white/5 bg-surface-card"
        }`}
      >
        <div className="flex items-center justify-between">
          <span className="text-base font-semibold">{title}</span>
          <span className={`h-5 w-5 rounded-full border ${selected ? "border-brand bg-brand" : "border-white/25"}`} />
        </div>
        <p className="mt-1.5 text-sm text-white/50">{desc}</p>
      </div>
    </button>
  );
}
